import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';

type QuickAction = {
  id: string;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
  status?: 'connected' | 'disconnected' | 'syncing';
  actionLabel?: string;
};

type QuickActionsWidgetProps = {
  actions: QuickAction[];
  onCreateTrip?: () => void;
  onAskAI?: () => void;
  onBrowseTemplates?: () => void;
  hasActiveTrips?: boolean;
};

export function QuickActionsWidget({
  actions,
  onCreateTrip,
  onAskAI,
  onBrowseTemplates,
  hasActiveTrips = false,
}: QuickActionsWidgetProps) {
  const theme = useTheme();

  const tonePalette = [
    { bg: theme.colors.primary + '10', border: theme.colors.primary, icon: theme.colors.primary },
    { bg: theme.colors.accent + '18', border: theme.colors.accent, icon: theme.colors.accent },
    { bg: theme.colors.success + '14', border: theme.colors.success, icon: theme.colors.success },
    { bg: theme.colors.warning + '14', border: theme.colors.warning, icon: theme.colors.warning },
  ];

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'connected':
        return theme.colors.success;
      case 'disconnected':
        return theme.colors.textSecondary;
      case 'syncing':
        return theme.colors.primary;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'connected':
        return 'checkmark-circle';
      case 'disconnected':
        return 'close-circle';
      case 'syncing':
        return 'sync';
      default:
        return undefined;
    }
  };

  return (
    <Card
      variant="subtle"
      elevation={0}
      padding={theme.spacing(1.25)}
      style={{
        backgroundColor: theme.colors.background,
        borderColor: theme.colors.borderMuted,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(0.75),
          marginBottom: theme.spacing(0.25),
        }}
      >
        <Ionicons name="flash-outline" size={18} color={theme.colors.primary} />
        <SubHeading style={{ letterSpacing: -0.1 }}>Quick actions</SubHeading>
      </View>
      <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.1) }}>
        Stay organized fast
      </Caption>

      {actions.length > 0 && (
        <View style={{ gap: theme.spacing(0.65) }}>
          {actions.map((action, index) => {
            const statusIcon = getStatusIcon(action.status);
            const statusColor = getStatusColor(action.status);
            const tone = tonePalette[index % tonePalette.length];

            return (
              <Pressable
                key={action.id}
                onPress={action.onPress}
                style={({ pressed }) => ({
                  width: '100%',
                  paddingVertical: theme.spacing(1),
                  paddingHorizontal: theme.spacing(1.1),
                  borderRadius: theme.radii.lg,
                  backgroundColor: tone.bg,
                  borderWidth: 1,
                  borderColor: tone.border,
                  transform: [{ scale: pressed ? 0.98 : 1 }],
                  opacity: pressed ? 0.96 : 1,
                })}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing(0.75),
                    marginBottom: theme.spacing(0.1),
                  }}
                >
                  <View
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: theme.radii.full,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: tone.icon + '1A',
                    }}
                  >
                    <Ionicons name={action.icon} size={16} color={tone.icon} />
                  </View>
                  <Body
                    style={{
                      fontWeight: '700',
                      color: theme.colors.text,
                      fontSize: 15,
                      flex: 1,
                    }}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {action.label}
                  </Body>
                  {statusIcon && (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(0.3),
                      }}
                    >
                      <Ionicons name={statusIcon as any} size={12} color={statusColor} />
                      {action.actionLabel && (
                        <Caption
                          color={action.status === 'syncing' ? theme.colors.primary : statusColor}
                          style={{ fontSize: 11, fontWeight: '700' }}
                        >
                          {action.actionLabel}
                        </Caption>
                      )}
                    </View>
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>
      )}
    </Card>
  );
}

